import { useTranslation } from 'react-i18next';
import Spinner from '../../../ui/Spinner';
import { BUDGETS, PACES, type Budget, type Pace } from '../types';

export interface InterestOption {
  label: string;
  emoji?: string;
}

interface Props {
  pace: Pace;
  budget: Budget;
  notes: string;
  interests: InterestOption[];
  interestsLoading: boolean;
  interestsError: string | null;
  selectedInterests: string[];
  onPace: (pace: Pace) => void;
  onBudget: (budget: Budget) => void;
  onNotes: (notes: string) => void;
  onToggleInterest: (label: string) => void;
}

function Segmented<T extends string>({ label, options, value, onChange, labelFor }: {
  label: string;
  options: T[];
  value: T;
  onChange: (value: T) => void;
  labelFor: (value: T) => string;
}) {
  return (
    <fieldset>
      <legend className="label">{label}</legend>
      <div className="grid gap-1 p-1 rounded-2xl bg-slate-100 dark:bg-slate-800" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
        {options.map(opt => (
          <button
            key={opt} type="button" onClick={() => onChange(opt)} aria-pressed={value === opt}
            className={`py-2 rounded-xl text-[13px] font-semibold transition-all cursor-pointer ${
              value === opt
                ? 'bg-white dark:bg-slate-900 text-teal-700 dark:text-teal-300 shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
            }`}
          >
            {labelFor(opt)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export default function StyleStep(props: Props) {
  const { t } = useTranslation();
  const k = 'dashboard.newTripWizard';
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Segmented label={t(`${k}.paceLabel`)} options={PACES} value={props.pace} onChange={props.onPace} labelFor={v => t(`${k}.pace.${v}`)} />
        <Segmented label={t(`${k}.budgetLabel`)} options={BUDGETS} value={props.budget} onChange={props.onBudget} labelFor={v => t(`${k}.budget.${v}`)} />
      </div>

      <fieldset>
        <legend className="label">{t(`${k}.interestsLabel`)}</legend>
        {props.interestsLoading ? (
          <p className="flex items-center gap-2 text-[13px] text-slate-500"><Spinner size="sm" />{t(`${k}.interestsLoading`)}</p>
        ) : props.interestsError ? (
          <p className="text-[13px] text-slate-500">{props.interestsError}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {props.interests.map(opt => {
              const active = props.selectedInterests.includes(opt.label);
              return (
                <button
                  key={opt.label} type="button" onClick={() => props.onToggleInterest(opt.label)} aria-pressed={active}
                  className={`px-3.5 py-2 rounded-full text-[13px] font-medium border transition-all cursor-pointer ${
                    active
                      ? 'bg-teal-600 border-teal-600 text-white'
                      : 'bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-teal-300'
                  }`}
                >
                  {opt.emoji && <span className="mr-1.5" aria-hidden>{opt.emoji}</span>}{opt.label}
                </button>
              );
            })}
          </div>
        )}
      </fieldset>

      <div>
        <label className="label" htmlFor="wizard-notes">{t(`${k}.notesLabel`)}</label>
        <textarea
          id="wizard-notes" className="input !rounded-2xl" rows={3} maxLength={2000}
          placeholder={t(`${k}.preferencesPlaceholder`)}
          value={props.notes}
          onChange={e => props.onNotes(e.target.value)}
        />
      </div>
    </div>
  );
}
